import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { HeroSection, PageRoot, StyledBreadCrumbs, StyledImage } from "./styles";
import heroBanner from "@/assets/heroBanner.png";
import ProductSection from "./ProductSection";
import NewArrivalSection from "./NewArrivalSection";
import IconWithTextSection from "./IconWithTextSection";
import Layout from "layout";
import { useGetProductsQuery, useGetPublicCollectionQuery } from "api/productApi";
import Loader from "shared/Loader";
import { labelForCategorySlug, PRODUCT_CATEGORY_TABS } from "utils/productCategory";
import {
  useCategorySectionScroll,
  useStableCatalogProducts,
} from "hooks/useCategoryCatalog";

const normalizeCategory = (value) =>
  PRODUCT_CATEGORY_TABS.includes(value) ? value : "all";

const items = [
  {
    title: <Link to="/home">Product</Link>,
  },
  {
    title: <span>All Products</span>,
  },
];

const BestSellingProductListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categorySlug, setCategorySlug] = useState(() =>
    normalizeCategory(searchParams.get("category") || "all")
  );
  const newArrivalsRef = useRef(null);
  const pendingFullRefreshRef = useRef(false);

  const handlePageChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  const isAllCategories = categorySlug === "all";

  useEffect(() => {
    setCategorySlug(normalizeCategory(searchParams.get("category") || "all"));
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: bestSellingData, isLoading: isLoadingAll, refetch: refetchAllProducts } =
    useGetProductsQuery(
      {
        page: currentPage,
        limit: pageSize,
        searchFilter: debouncedSearch || undefined,
      },
      { skip: !isAllCategories }
    );

  const { data: collectionData, isLoading: isLoadingCollection } =
    useGetPublicCollectionQuery(
      {
        slug: categorySlug,
        page: currentPage,
        limit: pageSize,
        searchFilter: debouncedSearch || undefined,
      },
      { skip: isAllCategories }
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [categorySlug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);
  const products = isAllCategories ? bestSellingData?.products : collectionData?.products;
  const total = isAllCategories ? bestSellingData?.total : collectionData?.total;
  const isLoading = isLoadingAll || isLoadingCollection;
  const { displayProducts, resetCatalogCache } = useStableCatalogProducts(products, isLoading);
  const { createCategoryChangeHandler, reanchorAfterLoad, scrollToSection } =
    useCategorySectionScroll(newArrivalsRef);
  const handleCategoryChange = useMemo(
    () => createCategoryChangeHandler(setCategorySlug, setCurrentPage),
    [createCategoryChangeHandler]
  );

  useEffect(() => {
    reanchorAfterLoad(isLoading);
  }, [isLoading, categorySlug, reanchorAfterLoad]);

  const refreshCatalog = useCallback(() => {
    pendingFullRefreshRef.current = true;
    resetCatalogCache();
    setCategorySlug("all");
    setCurrentPage(1);
  }, [resetCatalogCache]);

  useEffect(() => {
    if (!pendingFullRefreshRef.current || !isAllCategories) return;
    pendingFullRefreshRef.current = false;
    refetchAllProducts();
    requestAnimationFrame(scrollToSection);
  }, [isAllCategories, categorySlug, refetchAllProducts, scrollToSection]);

  useEffect(() => {
    if (location.state?.refreshCatalog) {
      refreshCatalog();
      navigate("/best-selling-products?category=all", { replace: true, state: {} });
    }
  }, [location.state, navigate, refreshCatalog]);

  const sectionHeading =
    categorySlug === "all"
      ? "All Products"
      : `All Products — ${labelForCategorySlug(categorySlug)}`;

  return (
    <Layout>
      <PageRoot>
        {/* <StyledBreadCrumbs items={items} /> */}
        <HeroSection>
          <StyledImage src={heroBanner} />
        </HeroSection>
        <ProductSection
          heading={sectionHeading}
          products={displayProducts}
          currentPage={currentPage}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
          searchValue={search}
          onSearchChange={setSearch}
        />
        <NewArrivalSection
          sectionRef={newArrivalsRef}
          products={displayProducts}
          categorySlug={categorySlug}
          onCategoryChange={handleCategoryChange}
          total={total}
          isLoading={isLoading}
        />
        <IconWithTextSection />
        <Loader isLoading={isLoading && displayProducts.length === 0} />
      </PageRoot>
    </Layout>
  );
};

export default BestSellingProductListPage;
